/* eslint-disable react/jsx-key */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React, { useEffect, useState } from 'react';
import { Collapse, Button, Tabs, Modal } from 'antd';
import '../shipment/ShipmentTrack.css';
import { LeftOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import { useAuth } from '@isocel/front-common';

import { usePackagingFlow } from '../../context/PackagingFlowContext';
import { usePackagingConfig } from '../../queries/package';
import { BaseModel, PackagingFlowDirection, PackagingFlowItem } from '../../queries/api/types';
import { useNotification } from '../notification/Notification';
import defaultImage from '../../assets/images/default.png';
import { SENDER_RECEIVER_MOVEMENT_TYPES } from '../../constants';

const { Panel } = Collapse;
const { TabPane } = Tabs;

const RecipientTrack: React.FC = () => {
    const history = useHistory();
    const { storeId } = useParams<{ storeId: string }>();
    const [pkgItemMap, setPkgItemMap] = useState<Record<string, PackagingFlowItem>>({});
    const [isModalVisible, setIsModalVisible] = useState(false);

    const {
        recipientLabel,
        supplierName,
        voucher,
        setPackageFlow,
        packageFlow,
        flowType,
        movementType,
        selectedProvider,
        selectedParc,
        transporterName,
        packageToEdit,
        setPackageToEdit,
        setIsPackageToEdit,
        isModifierClicked,
        selectedSender,
        selectedReciever,
    } = usePackagingFlow();
    const { user } = useAuth();
    const { data: packageConfig } = usePackagingConfig(user?.organization?.id);
    const { showNotification } = useNotification();
    const [activeKeys, setActiveKeys] = useState<string[]>([]);
    const [isPallateMovement, setPallateMovement] = useState(false);
    const [activePanelKey, setActivePanelKey] = useState<string | string[]>([]);
    useEffect(() => {
        if (packageConfig) {
            const flowMap: Record<string, PackagingFlowItem> = {};

            // Initialize flowMap with packages from packageConfig
            packageConfig.packages.forEach((pkg) => {
                flowMap[pkg.id] = {
                    package: pkg.id,
                    shippedQuantity: 0,
                    goodCount: 0,
                    badCount: 0,
                };
            });

            // Merge packages from packageFlow into flowMap
            if (packageFlow) {
                packageFlow.flowType = flowType;
                packageFlow.packages.forEach((pkg) => {
                    flowMap[pkg.package as string] = {
                        ...flowMap[pkg.package as string],
                        ...pkg,
                        goodCount: pkg.goodCount || 0,
                        badCount: pkg.badCount || 0,
                    };
                });
            } else {
                const flowItems = packageConfig.packages.map((pkg) => flowMap[pkg.id]);
                setPackageFlow({
                    sender: '',
                    recipient: '',
                    user: '',
                    loadingSession: '',
                    packages: flowItems,
                    flowType: flowType,
                });
            }
            const pallateMovement = [
                PackagingFlowDirection.Purchase,
                PackagingFlowDirection.ReceptionFromRepair,
                PackagingFlowDirection.Sale,
                PackagingFlowDirection.Destruction,
                PackagingFlowDirection.ShipmentForRepair,
                PackagingFlowDirection.Transfer,
                PackagingFlowDirection.Increase,
                PackagingFlowDirection.Decrease,
            ].includes(flowType);
            setPallateMovement(pallateMovement);
            setPkgItemMap(flowMap);
            if (isModifierClicked) {
                const openKeys = packageConfig.packages
                    .filter(
                        (pkg) =>
                            (flowMap[pkg.id]?.goodCount > 0 || flowMap[pkg.id]?.badCount > 0) &&
                            `${pkg.category.id}_${pkg.type.id}` // Unique key for category and type
                    )
                    .map((pkg) => `${pkg.category.id}_${pkg.type.id}`);
                setActiveKeys(openKeys);
            }
        }
    }, [flowType, isModifierClicked, packageConfig, packageFlow, setPackageFlow]);
    useEffect(() => {
        if (packageToEdit) {
            // Set the active panel key when packageToEdit is true
            setActivePanelKey(
                `${packageToEdit?.category?.id || 'unknown'}_${packageToEdit?.type?.id || 'unknown'}_panel`
            );
        }
    }, [packageToEdit]);

    const handlePanelChange = (key: string | string[]) => {
        setActivePanelKey(key); // Update the active panel key dynamically
    };
    const handleIncrement = (packageId: string, countType: 'goodCount' | 'badCount') => {
        if (!pkgItemMap[packageId]) return;
        const updatedMap = { ...pkgItemMap };
        updatedMap[packageId][countType] = updatedMap[packageId][countType] + 1;
        setPkgItemMap(updatedMap);
    };

    const handleDecrement = (packageId: string, countType: 'goodCount' | 'badCount') => {
        if (!pkgItemMap[packageId]) return;
        const updatedMap = { ...pkgItemMap };
        updatedMap[packageId][countType] = Math.max(0, updatedMap[packageId][countType] - 1);
        setPkgItemMap(updatedMap);
    };

    const handleInputChange = (packageId: string, countType: 'goodCount' | 'badCount', value: number) => {
        if (!pkgItemMap[packageId]) return;
        const updatedMap = { ...pkgItemMap };
        updatedMap[packageId][countType] = value;
        setPkgItemMap(updatedMap);
    };

    const handleClearCount = () => {
        if (!packageFlow) return;
        if (packageToEdit) {
            const updatedPackages = packageFlow.packages.map((pkg) =>
                pkg.package === packageToEdit.id ? { ...pkg, goodCount: 0, shippedQuantity: 0, badCount: 0 } : pkg
            );
            const clearedValue = Object.fromEntries(updatedPackages.map((pkg) => [pkg.package as string, pkg]));
            setPackageFlow({
                ...packageFlow,
                packages: updatedPackages,
            });
            setPkgItemMap(clearedValue);
        } else {
            const updatedPackages = packageFlow.packages.map((pkg) => ({
                ...pkg,
                goodCount: 0,
                badCount: 0,
            }));
            const clearedValue = Object.fromEntries(updatedPackages.map((pkg) => [pkg.package as string, pkg]));
            setPackageFlow({
                ...packageFlow,
                packages: updatedPackages,
            });
            setPkgItemMap(clearedValue);
        }
        setIsModalVisible(false);
    };

    const handleSave = () => {
        setPackageToEdit(null);
        if (packageFlow) {
            const hasValidCount = Object.values(pkgItemMap).some((pkg) => pkg.goodCount > 0 || pkg.badCount > 0);
            if (!hasValidCount) {
                showNotification('error', "Échec de l'analyse", `Veuillez saisir au moins un emballage.`);
                return;
            }
            const updatedPackages = Object.values(pkgItemMap);
            setPackageFlow({
                ...packageFlow,
                packages: updatedPackages,
                sender: packageFlow?.sender || '',
                recipient: packageFlow?.recipient || '',
                user: packageFlow?.user || '',
                loadingSession: packageFlow?.loadingSession || '',
                flowType: packageFlow?.flowType || flowType,
            });
            setIsPackageToEdit(false);
            history.push(`/recipientMovement/${storeId}`);
        }
    };

    const renderPackagesByCategory = (categoryId: string) => {
        const typesInCategory = packageConfig?.packages
            .filter((pkg) => pkg.category.id === categoryId)
            .reduce((acc, pkg) => {
                if (!acc.some((type) => type.id === pkg.type.id)) {
                    acc.push(pkg.type);
                }
                return acc;
            }, [] as BaseModel[]);

        return typesInCategory?.map((type) => (
            <div className="type-accordian">
                <Collapse
                    key={`${categoryId}_${type.id}`}
                    activeKey={activeKeys}
                    onChange={(keys) => setActiveKeys(keys as string[])}
                    style={{ marginTop: '10px' }}
                >
                    <Panel
                        header={
                            <div className="panel-header">
                                <img
                                    src={type.image || defaultImage}
                                    className="header-type-image"
                                    alt="package-type"
                                />
                                <span>{type.name}</span>
                            </div>
                        }
                        key={`${categoryId}_${type.id}`}
                    >
                        {packageConfig?.packages
                            .filter((pkg) => pkg.category.id === categoryId && pkg.type.id === type.id)
                            .map((pkg) => (
                                <div key={pkg.model.id} className="package-item flex-direction direction">
                                    <div className="model-view" style={{ marginBottom: '10px' }}>
                                        <strong>{pkg.model.name}</strong>
                                    </div>
                                    <div className="flex-direction flex direction">
                                        <div className="quantity-controls quantity w-full justify-between">
                                            <span className="good-count">Bon état</span>
                                            <MinusOutlined
                                                onClick={() => handleDecrement(pkg.id, 'goodCount')}
                                                className="minus"
                                            />
                                            <input
                                                type="number"
                                                value={pkgItemMap[pkg.id]?.goodCount || 0}
                                                min="0"
                                                className="input-box"
                                                onChange={(e) =>
                                                    handleInputChange(pkg.id, 'goodCount', parseInt(e.target.value))
                                                }
                                            />
                                            <PlusOutlined
                                                onClick={() => handleIncrement(pkg.id, 'goodCount')}
                                                className="plus"
                                            />
                                        </div>
                                        &nbsp; &nbsp;
                                        <div className="quantity-controls quantity w-full justify-between">
                                            <span className="good-count">Cassée</span>
                                            <MinusOutlined
                                                onClick={() => handleDecrement(pkg.id, 'badCount')}
                                                className="minus"
                                            />
                                            <input
                                                type="number"
                                                value={pkgItemMap[pkg.id]?.badCount || 0}
                                                min="0"
                                                className="input-box"
                                                onChange={(e) =>
                                                    handleInputChange(pkg.id, 'badCount', parseInt(e.target.value))
                                                }
                                            />
                                            <PlusOutlined
                                                onClick={() => handleIncrement(pkg.id, 'badCount')}
                                                className="plus"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </Panel>
                </Collapse>
            </div>
        ));
    };
    return (
        <>
            <div className="title mb-16">
                <LeftOutlined className="back-icon" onClick={() => history.goBack()} />
            </div>
            <div className="recepient-track  bg-dash shipment-track">
                <header className="header">
                    <div className="location">
                        {movementType && SENDER_RECEIVER_MOVEMENT_TYPES.includes(movementType.name) ? (
                            <>
                                <p>{movementType?.label}</p>
                                <p>Émetteur: {selectedSender?.name}</p>
                                <p>Destinataire: {selectedReciever?.name}</p>
                            </>
                        ) : isPallateMovement ? (
                            <>
                                <p>{movementType?.label}</p>
                                {selectedProvider?.name && <p>Prestataire: {selectedProvider.name}</p>}
                                <p>Parc palettes: {selectedParc?.name}</p>
                            </>
                        ) : (
                            <>
                                {recipientLabel && <p>Destinataire: {recipientLabel}</p>}
                                {supplierName && <p>Transporteur : {supplierName}</p>}
                                {transporterName && <p>Fournisseur: {transporterName}</p>}
                                {voucher && <p>Bon de transport: {voucher}</p>}
                            </>
                        )}
                    </div>
                </header>

                <Tabs
                    defaultActiveKey={packageToEdit?.category?.id ?? packageConfig?.packageCategories?.[0]?.id ?? '1'}
                >
                    {packageToEdit ? (
                        <TabPane
                            tab={`${packageToEdit?.category?.name ?? 'Unknown Category'}`}
                            key={`${packageToEdit?.category?.id}_panel`}
                        >
                            <Collapse
                                style={{ marginTop: '2px' }}
                                activeKey={activePanelKey}
                                onChange={handlePanelChange}
                            >
                                <Panel
                                    header={
                                        <div className="panel-header">
                                            <img
                                                src={packageToEdit?.type?.image || defaultImage}
                                                className="header-type-image"
                                                alt="package-type"
                                            />
                                            <span className="font-bold">
                                                {packageToEdit?.type?.name || 'Unknown Type'}
                                            </span>
                                        </div>
                                    }
                                    key={`${packageToEdit?.category?.id || 'unknown'}_${
                                        packageToEdit?.type?.id || 'unknown'
                                    }_panel`}
                                >
                                    <div key={packageToEdit?.id} className="package-item flex-direction direction">
                                        <div className="model-view" style={{ marginBottom: '10px' }}>
                                            <strong>{packageToEdit?.model?.name || 'Unknown Model'}</strong>
                                        </div>
                                        <div className="flex-direction flex direction">
                                            <div className="quantity-controls quantity w-full justify-between">
                                                <span className="good-count">Bon état</span>
                                                <MinusOutlined
                                                    onClick={() => handleDecrement(packageToEdit.id, 'goodCount')}
                                                    className="minus"
                                                />
                                                <input
                                                    type="number"
                                                    value={pkgItemMap[packageToEdit.id]?.goodCount || 0}
                                                    min="0"
                                                    className="input-box"
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            packageToEdit.id,
                                                            'goodCount',
                                                            parseInt(e.target.value, 10)
                                                        )
                                                    }
                                                />
                                                <PlusOutlined
                                                    onClick={() => handleIncrement(packageToEdit.id, 'goodCount')}
                                                    className="plus"
                                                />
                                            </div>
                                            &nbsp; &nbsp;
                                            <div className="quantity-controls quantity w-full justify-between">
                                                <span className="good-count">Cassée</span>
                                                <MinusOutlined
                                                    onClick={() => handleDecrement(packageToEdit.id, 'badCount')}
                                                    className="minus"
                                                />
                                                <input
                                                    type="number"
                                                    value={pkgItemMap[packageToEdit?.id]?.badCount || 0}
                                                    min="0"
                                                    className="input-box"
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            packageToEdit.id,
                                                            'badCount',
                                                            parseInt(e.target.value, 10)
                                                        )
                                                    }
                                                />
                                                <PlusOutlined
                                                    onClick={() => handleIncrement(packageToEdit?.id, 'badCount')}
                                                    className="plus"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </Panel>
                            </Collapse>
                        </TabPane>
                    ) : (
                        packageConfig?.packageCategories
                            .filter((category) => packageConfig.packages.some((pkg) => pkg.category.id === category.id))
                            .map((category) => (
                                <TabPane tab={category.name} key={category.id}>
                                    {renderPackagesByCategory(category.id)}
                                </TabPane>
                            ))
                    )}
                </Tabs>
            </div>
            <div className="bottom">
                <footer className="flex items-center justify-center">
                    <Button className="mr-16" onClick={() => setIsModalVisible(true)}>
                        Réinitialiser
                    </Button>
                    <Button type="primary" onClick={handleSave}>
                        Valider
                    </Button>
                </footer>
                <Modal
                    title="Supprimer les données"
                    visible={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    closable={false}
                    centered
                    width={360}
                    footer={[
                        <div className="flex items-center justify-center">
                            <Button className="mr-16" onClick={() => setIsModalVisible(false)}>
                                Annuler
                            </Button>
                            <Button type="primary" onClick={handleClearCount}>
                                Supprimer
                            </Button>
                        </div>,
                    ]}
                    aria-hidden={isModalVisible}
                >
                    <div className="text-black font-14">
                        Vous êtes sur le point de réinitialiser les supports et emballages. Souhaitez-vous continuer ?
                    </div>
                </Modal>
            </div>
        </>
    );
};

export default RecipientTrack;
